import { useCallback } from 'react';
import { Box } from '@mui/material';
import { ApolloError } from '@apollo/client';

import {
  Typography,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TableContainer,
  TableHeader,
  Pagination,
  SearchForm,
  SearchFormProps,
  LinearProgress,
  LoadedContent,
  NO_DATA_PLACEHOLDER,
} from '@occc/ui-design-system';

import { IPaginationProps } from '@occc-app/hooks/usePaginatedQuery';
import { ExternalLoyaltyManualOfferLocation } from '@occc/graphql';

type IncludedStoresTableProps = {
  locations?: ExternalLoyaltyManualOfferLocation[];
  loading: boolean;
  error?: ApolloError;
  paginationProps: IPaginationProps;
  search: string;
  onSearch: (value: string) => void;
};

export const IncludedStoresTable = ({
  locations,
  loading,
  error,
  paginationProps,
  search,
  onSearch,
}: IncludedStoresTableProps) => {
  const handleSearchSubmit = useCallback<SearchFormProps['onSubmit']>(
    ({ search: value }) => onSearch(value ?? ''),
    [onSearch],
  );

  const renderData = (items: ExternalLoyaltyManualOfferLocation[]) => (
    <>
      {loading && <LinearProgress />}
      <Box sx={{ overflowX: 'auto' }}>
        <Table>
          <TableHead>
            <TableRow sx={{ whiteSpace: 'nowrap' }}>
              <TableCell>Site ID</TableCell>
              <TableCell>Site Name (DBA)</TableCell>
              <TableCell>Site Address</TableCell>
              <TableCell>RCN or RJR Account Number</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {items.map((location) => (
              <TableRow key={location.uuid}>
                <TableCell>
                  {location.siteId ? (
                    location.siteId
                  ) : (
                    <Typography variant='body2' color='error'>
                      Site not found
                    </Typography>
                  )}
                </TableCell>
                <TableCell>
                  {location.siteName ? (
                    location.siteName
                  ) : (
                    <Typography variant='body2' color='error'>
                      Site not found
                    </Typography>
                  )}
                </TableCell>
                <TableCell>{location.siteAddress ?? NO_DATA_PLACEHOLDER}</TableCell>
                <TableCell>{location.outletId ?? NO_DATA_PLACEHOLDER}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Box>
      <Pagination {...paginationProps} />
    </>
  );

  return (
    <TableContainer>
      <TableHeader
        title='Included Stores'
        actions={<SearchForm onSubmit={handleSearchSubmit} initialValues={{ search }} />}
      />
      <LoadedContent<ExternalLoyaltyManualOfferLocation>
        loading={loading}
        error={error}
        items={locations}
        renderData={renderData}
      />
    </TableContainer>
  );
};
